import type { SceneryArrays } from './types';

/**
 * scenery/finishClear.ts — the FINISH PLAZA CLEARING (M97, pure + unit-tested).
 *
 * Zone builders place scenery right up to the finish line, which used to leave
 * trees inside Tower Bridge's towers, pines through the Galleria arcades,
 * villas clipping the Lekki pylon and a snow hill bulging onto the road at the
 * line (all measured in-game). Rather than touch the builders (their rng
 * SEQUENCE is each city's layout — reordering reshuffles the whole city), this
 * runs AFTER them and deletes offending instances: determinism preserved,
 * every other placement byte-identical.
 *
 * Three rules, tuned against the measured gateway + grandstand footprints:
 *  - FORECOURT: no vertical structure inside the finish gateway's block —
 *    closer than `clearBack` before the line and within `clearLat` of the
 *    centreline. (Ground sheets, water and people stay: crowds AT the line
 *    are the point of a finish.)
 *  - ROAD EDGE: on the final approach nothing may overhang the road itself —
 *    wide-radius hills (and oversized canopies) whose EDGE crosses the
 *    guardrail are dropped even when their centre sits well off-course.
 *  - STAND FOOTPRINTS (GS3): the plaza's grandstands stand on fixed pads on
 *    the land side(s) of the approach + rollout. Trees whose canopy reaches
 *    into a pad (Napoli grew a blob tree THROUGH a canopy — measured), hills
 *    whose dome edge crosses one (Patagonia's snow hill swallowed a canopy
 *    corner — measured) and any vertical prop standing on one are dropped.
 *    STRICTLY DELETE-ONLY, like everything in this file.
 */

export interface FinishClearOpts {
  /** How far before the line the forecourt clearing starts (units). */
  clearBack: number;
  /** Half-width of the forecourt corridor (from the centreline, units). */
  clearLat: number;
  /** How far before the line the road-edge (overhang) rule starts. */
  edgeBack: number;
}

export const FINISH_CLEAR: FinishClearOpts = {
  clearBack: 36, // covers the longest gateway (the Galleria hall spans ±34)
  clearLat: 34, // road half-width 18 + the gateway leg band (out to ~33)
  edgeBack: 85,
};

/** Per-city forecourt overrides. Mumbai's rebuilt Gateway of India (the
 *  graphics-overhaul Blender round) spreads its flanking wings to |lateral|
 *  ≈ 44 — past the global ±34 corridor — so its forecourt clears to ±46 or
 *  an unlucky seed can stand a Colaba block INSIDE a wing. Delete-only law
 *  unchanged; every other city keeps the global numbers. */
export const FINISH_CLEAR_CITY: Record<string, FinishClearOpts> = {
  mumbai: { ...FINISH_CLEAR, clearLat: 46 },
};

/** Which sides of the finish approach are LAND (from the city survey — the
 *  other side is open water there: Thames, Tokyo Bay, the Lagos lagoon, Lac
 *  Rose's shore band, Patagonia meltwater). Stands only ever go on land.
 *  Lives HERE (the pure module) so the clearing rules and the plaza builder
 *  can never disagree about where a stand may exist. */
export const STAND_SIDES: Record<string, readonly (-1 | 1)[]> = {
  nyc: [-1, 1],
  london: [-1],
  napoli: [-1, 1],
  tokyo: [-1],
  rio: [-1, 1],
  dakar: [-1],
  lagos: [-1],
  mumbai: [-1, 1],
  patagonia: [-1],
};

/** The grandstand pads' shared geometry (mirrored by finishPlaza.ts — one
 *  source of truth for the clearing AND the builder). All in track units:
 *  `alongs` are offsets from the finish line, the lateral band is measured
 *  outward from the road half-width. `latFrom/latTo` cover base skirt →
 *  canopy back edge with a small margin. */
export const STAND_LAYOUT = {
  alongs: [-56, -33, 21] as readonly number[],
  /** Stand length along the road (canopy adds ~1 each end — margin covers it). */
  len: 17,
  /** Lateral band occupied by skirt + tiers + canopy, as offsets from hw. */
  latFrom: 4.9,
  latTo: 17.5,
  /** Along-track margin added around each pad for the intersection tests. */
  margin: 2,
  /** shared-2a: extra outward clearance a HILL DOME must keep behind a stand.
   *  The pad band ends at the canopy's back edge, so a dome centred just past
   *  it passed the test and still stood pressed against the grandstand's
   *  back — measured on the Patagonia rollout. A stand needs air behind it. */
  hillBackClear: 7,
  /** shared-2a: a tree counts as BURIED (and is dropped) when a surviving hill
   *  dome covers more than this fraction of its height. Named so an A/B probe
   *  can disable the rule without editing code. */
  buriedFrac: 0.5,
  /** How far back from the line the buried-tree rule reaches. Wider than
   *  FINISH_CLEAR.edgeBack (85) on purpose: the rollout camera looks back
   *  PAST the -56 stand, and the measured half-sunk pines sit at along -87
   *  and -98 — i.e. in the plaza's backdrop, not on its apron. */
  buriedBack: 150,
};

/** Minimal projection of a world (x, z) onto the track — what Track.project
 *  returns, narrowed so tests can stub it with plain math. */
export type ProjectFn = (x: number, z: number) => { distance: number; lateral: number };

/** Instance arrays swept by the forecourt rule: everything that stands UP.
 *  Ground sheets (grass/sand/ponds/promenade), water props (boats/icebergs)
 *  and people are deliberately NOT listed. */
const VERTICAL_KEYS = [
  'buildings',
  'plain',
  'adBuildings',
  'roofs',
  'neon',
  'adScreens',
  'vertSigns',
  'trees',
  'palms',
  'pines',
  'baobabs',
  'stalls',
  'umbrellas',
  'scooters',
  'cabs',
  'buses',
  'phoneBoxes',
  'stiltHouses',
  'lanterns',
  'laundry',
  'fences',
  'saltMounds',
] as const;

/** Distance² from a projected point to the nearest grandstand pad, minus 0 —
 *  returns true when a circle of radius `r` around (d, lat) touches any pad.
 *  Pads are rectangles in track space: along ∈ finish+along ± (len/2+margin),
 *  lateral ∈ side·(hw+latFrom … hw+latTo). */
function hitsStandPad(
  d: number,
  lat: number,
  r: number,
  finish: number,
  hw: number,
  sides: readonly (-1 | 1)[],
  outPad = 0,
): boolean {
  const { alongs, len, latFrom, latTo, margin } = STAND_LAYOUT;
  for (const side of sides) {
    // signed lateral flipped onto the stand's side: positive = outward there.
    const out = side * lat;
    const dLat = Math.max(hw + latFrom - out, out - (hw + latTo + outPad), 0);
    if (dLat >= r) continue;
    for (const along of alongs) {
      const dAlong = Math.max(Math.abs(d - (finish + along)) - (len / 2 + margin), 0);
      if (dAlong * dAlong + dLat * dLat < r * r) return true;
    }
  }
  return false;
}

/** Clear the finish plaza. `finish` = the line's arc distance, `hw` = road
 *  half-width. `cityId` selects the city's stand sides for the pad rule
 *  (unknown/omitted = both sides, the safe superset). Mutates `a` in place
 *  (runs once at build time). */
export function clearFinishZone(
  a: SceneryArrays,
  project: ProjectFn,
  finish: number,
  hw: number,
  cityId?: string,
  optsIn?: FinishClearOpts,
): void {
  const sides = STAND_SIDES[cityId ?? ''] ?? ([-1, 1] as const);
  // Explicit opts win (tests); otherwise the city's override, then the global.
  const opts = optsIn ?? FINISH_CLEAR_CITY[cityId ?? ''] ?? FINISH_CLEAR;

  const inForecourt = (p: { distance: number; lateral: number }): boolean =>
    p.distance - finish > -opts.clearBack && Math.abs(p.lateral) < opts.clearLat;

  for (const key of VERTICAL_KEYS) {
    const list = a[key];
    for (let i = list.length - 1; i >= 0; i--) {
      const inst = list[i] as { x: number; z: number };
      const p = project(inst.x, inst.z);
      if (inForecourt(p) || hitsStandPad(p.distance, p.lateral, 3, finish, hw, sides))
        list.splice(i, 1);
    }
  }

  // Road-edge rule: hills are placed by CENTRE but reach `r` sideways — a big
  // dome centred 36 off the road can still bulge over the rail (measured in
  // Patagonia at the line). Near the finish, drop any hill whose edge crosses
  // into the road corridor. GS3 adds the stand-pad test: a dome edge reaching
  // into a grandstand pad swallowed the Patagonia rollout canopy (measured).
  for (let i = a.hills.length - 1; i >= 0; i--) {
    const h = a.hills[i];
    const p = project(h.x, h.z);
    const along = p.distance - finish;
    const overPad = hitsStandPad(
      p.distance,
      p.lateral,
      h.r,
      finish,
      hw,
      sides,
      STAND_LAYOUT.hillBackClear,
    );
    if (along <= -opts.edgeBack && !overPad) continue;
    if (overPad || Math.abs(p.lateral) - h.r < hw + 4) a.hills.splice(i, 1);
  }

  // Same idea for the tree families' canopies on the last approach (the zone
  // forests already keep an inner margin mid-course — this only guards the
  // final stretch where a stray canopy would hang over the line) — plus the
  // GS3 stand-pad test so no canopy pokes through a grandstand roof.
  const canopyLists = [a.trees, a.palms, a.pines, a.baobabs] as const;
  for (const list of canopyLists) {
    for (let i = list.length - 1; i >= 0; i--) {
      const t = list[i] as { x: number; z: number; s?: number };
      const p = project(t.x, t.z);
      const canopy = 4 * (t.s ?? 1);
      if (hitsStandPad(p.distance, p.lateral, canopy + 0.5, finish, hw, sides)) {
        list.splice(i, 1);
        continue;
      }
      const along = p.distance - finish;
      // shared-2a — BURIED IN A DOME. Trees are planted at ROAD grade while
      // hills rise over them from their own centres, so a tree inside a dome's
      // footprint is swallowed to whatever depth the dome has there (measured
      // on the Patagonia rollout: a pine sticking out of a snow dome's flank
      // like a splinter). This runs AFTER the hill sweep above, so it only
      // ever tests domes that survived, and it is scoped to the finish window
      // the rest of this file owns. Mid-course burial is a much bigger,
      // separate problem (it needs the trees LIFTED, not deleted).
      if (along > -STAND_LAYOUT.buriedBack && isBuried(t, a.hills)) {
        list.splice(i, 1);
        continue;
      }
      if (along <= -opts.edgeBack) continue;
      if (Math.abs(p.lateral) - canopy < hw + 1) list.splice(i, 1);
    }
  }
}

/** Is this tree swallowed by a hill dome? The dome is a half-ellipsoid of
 *  radius `r` and height `h` centred on the hill; at horizontal distance `dd`
 *  its surface stands `h·√(1−(dd/r)²)` above grade. More than
 *  STAND_LAYOUT.buriedFrac of the tree's height under that = buried. Tree
 *  height is taken as 6× its scale (the canopy radius above uses 4×). */
function isBuried(
  t: { x: number; z: number; s?: number },
  hills: readonly { x: number; z: number; r: number; h: number }[],
): boolean {
  const half = 6 * (t.s ?? 1) * STAND_LAYOUT.buriedFrac;
  for (const h of hills) {
    const dd = Math.hypot(t.x - h.x, t.z - h.z);
    if (dd >= h.r) continue;
    if (h.h * Math.sqrt(1 - (dd / h.r) * (dd / h.r)) > half) return true;
  }
  return false;
}
